import React, { useId } from 'react'

import './Loading.css'

export type LoadingVariant = 'spinner' | 'logo' | 'dots'

export interface LoadingProps {
  variant?: LoadingVariant
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

// Colours come from global --loading-accent-1 / --loading-accent-2 when set by the host, else fallbacks
const LOADING_ACCENT_1 = 'var(--loading-accent-1, #184CE8)'
const LOADING_ACCENT_2 = 'var(--loading-accent-2, #9164EE)'

export const Loading: React.FC<LoadingProps> = ({ variant = 'spinner', className = '', size = 'md' }) => {
  const id = useId()
  const colour1 = LOADING_ACCENT_1
  const colour2 = LOADING_ACCENT_2

  if (variant === 'dots') {
    const sizeClasses = {
      sm: 'w-12',
      md: 'w-16',
      lg: 'w-20',
    }

    const dotSizeClasses = {
      sm: 'w-2 h-2',
      md: 'w-2.5 h-2.5',
      lg: 'w-3 h-3',
    }

    const dotsGlowClasses = {
      sm: 'dots-glow-sm',
      md: 'dots-glow-md',
      lg: 'dots-glow-lg',
    }

    // Generate unique gradient ID to avoid conflicts
    const gradientId = `dots-gradient-${size}-${id}`

    return (
      <div
        data-testid="loading"
        role="status"
        className={`flex items-center justify-center gap-1.5 ${className}`}
      >
        <span className="sr-only">Loading...</span>
        <div
          aria-hidden="true"
          className={`${sizeClasses[size]} flex items-center justify-center gap-1.5`}
        >
          <svg
            className="absolute opacity-0"
            width="0"
            height="0"
          >
            <defs>
              <linearGradient
                id={gradientId}
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  stopColor={colour1}
                />
                <stop
                  offset="100%"
                  stopColor={colour2}
                />
              </linearGradient>
            </defs>
          </svg>
          <div
            className={`${dotSizeClasses[size]} rounded-full dots-loading dots-loading-1 ${dotsGlowClasses[size]}`}
            style={{
              background: `linear-gradient(135deg, ${colour1} 0%, ${colour2} 100%)`,
            }}
          />
          <div
            className={`${dotSizeClasses[size]} rounded-full dots-loading dots-loading-2 ${dotsGlowClasses[size]}`}
            style={{
              background: `linear-gradient(135deg, ${colour1} 0%, ${colour2} 100%)`,
            }}
          />
          <div
            className={`${dotSizeClasses[size]} rounded-full dots-loading dots-loading-3 ${dotsGlowClasses[size]}`}
            style={{
              background: `linear-gradient(135deg, ${colour1} 0%, ${colour2} 100%)`,
            }}
          />
        </div>
      </div>
    )
  }

  if (variant === 'logo') {
    const sizeClasses = {
      sm: 'size-24',
      md: 'size-32',
      lg: 'size-48',
    }

    // Generate unique gradient ID to avoid conflicts if multiple logos are rendered
    const gradientId = `logo-gradient-${size}-${id}`

    return (
      <div
        data-testid="loading"
        role="status"
        className={`flex items-center justify-center ${className}`}
      >
        <span className="sr-only">Loading...</span>
        <div
          aria-hidden="true"
          className={`${sizeClasses[size]}`}
        >
          <svg
            width="157"
            height="45"
            viewBox="0 0 157 45"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-full object-contain logo-loading"
          >
            <defs>
              <linearGradient
                id={gradientId}
                x1="0"
                y1="0"
                x2="39.3792"
                y2="44.9758"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  stopColor="white"
                  className="logo-gradient-stop"
                />
              </linearGradient>
            </defs>
            {/* Anchor icon - white gradient, will be inverted in light mode */}
            <path
              d="M21.4189 19.9395L22.374 42.9932L19.8164 45.001L17.2568 42.9932C13.8156 42.7293 10.4965 41.5984 7.60938 39.7061C4.32761 37.5542 1.69603 34.5452 0 31.0049L2.31445 29.9473C3.79408 32.3585 6.07757 34.4044 8.91211 35.8574C10.7626 36.7969 12.7423 37.4565 14.7861 37.8154C15.6635 37.9705 16.5491 38.0749 17.4385 38.127L18.1914 19.9365H21.3975L21.4189 19.9395ZM39.6094 31.0107C37.914 34.5517 35.2825 37.5607 32 39.7119C29.8684 41.1086 27.4953 42.0959 25.002 42.6211C24.7737 42.6697 24.5429 42.7137 24.3125 42.7539L24.1143 37.9277C24.3445 37.8939 24.5746 37.8575 24.8027 37.8174C26.8505 37.459 28.8338 36.798 30.6875 35.8564C33.524 34.4035 35.8045 32.3583 37.2842 29.9473L39.6094 31.0107ZM19.7949 0C21.6634 0.008435 23.4609 0.718106 24.832 1.98828C25.4456 2.55547 25.9506 3.22997 26.3223 3.97852C27.3823 4.10004 28.4033 4.45179 29.3135 5.00879L29.4219 5.07617L29.4473 5.09277C30.4444 5.71247 31.2694 6.57398 31.8467 7.59668C32.424 8.61935 32.7353 9.77088 32.751 10.9453C32.7509 11.2281 32.7339 11.5112 32.7002 11.792C32.6748 12.0033 32.6381 12.2145 32.5938 12.4258C31.9047 15.6386 28.808 17.9658 25.5254 17.9658H6.51855C5.46285 17.9569 4.42087 17.7231 3.46191 17.2812C2.50296 16.8393 1.64811 16.1993 0.955078 15.4023H25.2256C25.9918 15.4019 26.7445 15.1955 27.4043 14.8057C28.0639 14.4159 28.6072 13.8565 28.9775 13.1855V13.1689L29.0049 13.1152C29.2132 12.73 29.3654 12.317 29.457 11.8887C29.481 11.7787 29.5006 11.6685 29.5146 11.5586C29.5423 11.357 29.5562 11.1537 29.5566 10.9502C29.5566 9.03825 28.5994 7.409 27.0205 6.77441V7.01758C27.0178 7.64167 26.9152 8.26153 26.7178 8.85352C26.3348 9.9667 25.7127 10.9831 24.8955 11.8301L23.4033 10.4209C23.6748 9.8227 23.8667 9.19136 23.9727 8.54297C24.1423 7.5702 24.0851 6.57107 23.8057 5.62402C23.5119 4.72322 22.9483 3.934 22.1924 3.36328C21.4366 2.79277 20.5242 2.46815 19.5781 2.43262C18.9143 2.39633 18.2491 2.48475 17.6182 2.69434C17.1651 2.49881 16.6962 2.34151 16.2168 2.22461C15.7813 2.11691 15.3383 2.04251 14.8916 2.00098C16.2011 0.717134 17.9616 -0.00141454 19.7949 0ZM38.6348 15.3955C37.9411 16.1923 37.0862 16.8326 36.127 17.2744C35.1676 17.7162 34.1254 17.9499 33.0693 17.959H31.2373C31.6362 17.6453 32.0091 17.2994 32.3516 16.9248C32.7006 16.5505 33.0168 16.1466 33.2979 15.7188C33.3781 15.6067 33.4443 15.5033 33.5098 15.3955H38.6348ZM14.4072 3.94238C16.2172 4.0299 17.9222 4.81931 19.1611 6.14258L17.6689 7.54883C16.6507 6.91476 15.4854 6.55579 14.2871 6.50781H14.0625C11.6152 6.50806 10.0471 8.4961 10.0469 10.9492C10.0469 11.265 10.0811 11.5802 10.1484 11.8887C10.2627 12.4374 10.4774 12.9604 10.7822 13.4307H7.29883C7.16884 13.1087 7.06456 12.7768 6.98633 12.4385C6.88241 11.9519 6.83013 11.4556 6.83008 10.958C6.83016 7.0896 10.1885 3.94238 14.0566 3.94238H14.4072Z"
              fill={`url(#${gradientId})`}
              className="logo-anchor logo-anchor-swing"
            />
            {/* "Ankor" text - white, will be inverted to black in light mode */}
            <path
              d="M54.2234 13.96H57.5834L63.8474 31H60.4394L58.8554 26.752H52.9034L51.3434 31H47.9354L54.2234 13.96ZM58.2554 24.424L55.9034 17.392L53.4554 24.424H58.2554ZM77.0924 31H73.8764V23.944C73.8764 22.936 73.7004 22.2 73.3484 21.736C72.9964 21.272 72.5084 21.04 71.8844 21.04C71.4524 21.04 71.0124 21.152 70.5644 21.376C70.1324 21.6 69.7404 21.912 69.3884 22.312C69.0364 22.696 68.7804 23.144 68.6204 23.656V31H65.4044V18.424H68.3084V20.752C68.6284 20.208 69.0364 19.752 69.5324 19.384C70.0284 19 70.5964 18.712 71.2364 18.52C71.8924 18.312 72.5804 18.208 73.3004 18.208C74.0844 18.208 74.7244 18.352 75.2204 18.64C75.7324 18.912 76.1164 19.288 76.3724 19.768C76.6444 20.232 76.8284 20.76 76.9244 21.352C77.0364 21.944 77.0924 22.544 77.0924 23.152V31ZM88.4808 31L84.8088 25.576L83.1048 27.256V31H79.8888V13.48H83.1048V24.088L88.1688 18.448H91.5768L86.8968 23.776L91.9128 31H88.4808ZM98.4234 31.24C97.3994 31.24 96.4794 31.072 95.6634 30.736C94.8474 30.384 94.1514 29.904 93.5754 29.296C93.0154 28.688 92.5834 27.992 92.2794 27.208C91.9754 26.424 91.8234 25.6 91.8234 24.736C91.8234 23.856 91.9754 23.024 92.2794 22.24C92.5834 21.456 93.0154 20.76 93.5754 20.152C94.1514 19.544 94.8474 19.072 95.6634 18.736C96.4794 18.384 97.3994 18.208 98.4234 18.208C99.4474 18.208 100.359 18.384 101.159 18.736C101.975 19.072 102.671 19.544 103.247 20.152C103.823 20.76 104.255 21.456 104.543 22.24C104.847 23.024 104.999 23.856 104.999 24.736C104.999 25.6 104.847 26.424 104.543 27.208C104.255 27.992 103.823 28.688 103.247 29.296C102.687 29.904 101.999 30.384 101.183 30.736C100.367 31.072 99.4474 31.24 98.4234 31.24ZM95.1354 24.736C95.1354 25.472 95.2794 26.128 95.5674 26.704C95.8554 27.264 96.2474 27.704 96.7434 28.024C97.2394 28.344 97.7994 28.504 98.4234 28.504C99.0314 28.504 99.5834 28.344 100.079 28.024C100.575 27.688 100.967 27.24 101.255 26.68C101.559 26.104 101.711 25.448 101.711 24.712C101.711 23.992 101.559 23.344 101.255 22.768C100.967 22.192 100.575 21.744 100.079 21.424C99.5834 21.104 99.0314 20.944 98.4234 20.944C97.7994 20.944 97.2394 21.112 96.7434 21.448C96.2474 21.768 95.8554 22.216 95.5674 22.792C95.2794 23.352 95.1354 24 95.1354 24.736ZM114.658 21.208C113.682 21.208 112.81 21.4 112.042 21.784C111.274 22.152 110.722 22.688 110.386 23.392V31H107.17V18.424H110.122V21.112C110.57 20.248 111.138 19.568 111.826 19.072C112.514 18.576 113.242 18.304 114.01 18.256C114.186 18.256 114.322 18.256 114.418 18.256C114.514 18.256 114.594 18.264 114.658 18.28V21.208Z"
              fill="white"
              className="logo-ankor"
            />
            {/* "Pay" text - orange, stays orange in both themes */}
            <path
              d="M116.763 31V13.96H123.987C124.771 13.96 125.491 14.12 126.147 14.44C126.803 14.76 127.371 15.192 127.851 15.736C128.347 16.28 128.731 16.888 129.003 17.56C129.275 18.232 129.411 18.92 129.411 19.624C129.411 20.584 129.187 21.496 128.739 22.36C128.291 23.224 127.675 23.928 126.891 24.472C126.107 25.016 125.179 25.288 124.107 25.288H120.075V31H116.763ZM120.075 22.384H123.915C124.315 22.384 124.675 22.272 124.995 22.048C125.315 21.824 125.571 21.504 125.763 21.088C125.955 20.656 126.051 20.168 126.051 19.624C126.051 19.032 125.939 18.528 125.715 18.112C125.507 17.696 125.227 17.384 124.875 17.176C124.539 16.968 124.171 16.864 123.771 16.864H120.075V22.384ZM130.376 27.28C130.376 26.464 130.6 25.752 131.048 25.144C131.512 24.52 132.152 24.04 132.968 23.704C133.784 23.368 134.72 23.2 135.776 23.2C136.304 23.2 136.84 23.24 137.384 23.32C137.928 23.4 138.4 23.528 138.8 23.704V23.032C138.8 22.232 138.56 21.616 138.08 21.184C137.616 20.752 136.92 20.536 135.992 20.536C135.304 20.536 134.648 20.656 134.024 20.896C133.4 21.136 132.744 21.488 132.056 21.952L131.024 19.84C131.856 19.296 132.704 18.888 133.568 18.616C134.448 18.344 135.368 18.208 136.328 18.208C138.12 18.208 139.512 18.656 140.504 19.552C141.512 20.448 142.016 21.728 142.016 23.392V27.4C142.016 27.736 142.072 27.976 142.184 28.12C142.312 28.264 142.512 28.352 142.784 28.384V31C142.496 31.048 142.232 31.088 141.992 31.12C141.768 31.152 141.576 31.168 141.416 31.168C140.776 31.168 140.288 31.024 139.952 30.736C139.632 30.448 139.432 30.096 139.352 29.68L139.28 29.032C138.736 29.736 138.056 30.28 137.24 30.664C136.424 31.048 135.592 31.24 134.744 31.24C133.912 31.24 133.16 31.072 132.488 30.736C131.832 30.384 131.312 29.912 130.928 29.32C130.56 28.712 130.376 28.032 130.376 27.28ZM138.2 27.928C138.376 27.736 138.52 27.544 138.632 27.352C138.744 27.16 138.8 26.984 138.8 26.824V25.552C138.416 25.392 138 25.272 137.552 25.192C137.104 25.096 136.68 25.048 136.28 25.048C135.448 25.048 134.76 25.232 134.216 25.6C133.688 25.952 133.424 26.424 133.424 27.016C133.424 27.336 133.512 27.64 133.688 27.928C133.864 28.216 134.12 28.448 134.456 28.624C134.792 28.8 135.192 28.888 135.656 28.888C136.136 28.888 136.608 28.8 137.072 28.624C137.536 28.432 137.912 28.2 138.2 27.928ZM144.817 33.448C145.105 33.512 145.369 33.568 145.609 33.616C145.865 33.664 146.081 33.688 146.257 33.688C146.561 33.688 146.809 33.608 147.001 33.448C147.209 33.304 147.409 33.032 147.601 32.632C147.793 32.248 147.993 31.704 148.201 31L143.233 18.424H146.569L149.953 27.712L152.929 18.424H156.001L150.721 33.496C150.529 34.056 150.233 34.56 149.833 35.008C149.449 35.472 148.961 35.832 148.369 36.088C147.793 36.36 147.145 36.496 146.425 36.496C146.169 36.496 145.913 36.472 145.657 36.424C145.401 36.392 145.121 36.328 144.817 36.232V33.448Z"
              fill="#FD794E"
              className="logo-pay"
            />
          </svg>
        </div>
      </div>
    )
  }

  // Spinner variant (default)

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  }

  const spinnerGlowClasses = {
    sm: 'spinner-glow-sm',
    md: 'spinner-glow-md',
    lg: 'spinner-glow-lg',
  }

  const segmentClasses = {
    sm: 'spinner-segment-sm',
    md: 'spinner-segment-md',
    lg: 'spinner-segment-lg',
  }

  // Create 8 segments for the spinner
  const segments = Array.from({ length: 8 }, (_, i) => i)

  return (
    <div
      data-testid="loading"
      role="status"
      className={`flex items-center justify-center ${className}`}
    >
      <div className={`${sizeClasses[size]} ${spinnerGlowClasses[size]} relative`}>
        <svg
          aria-hidden="true"
          className="w-full h-full"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient
              id={`spinner-gradient-${size}`}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop
                offset="0%"
                stopColor={colour1}
              />
              <stop
                offset="100%"
                stopColor={colour2}
              />
            </linearGradient>
          </defs>
          {segments.map((segment) => {
            const angle = (segment * 360) / 8
            const startAngle = angle - 20
            const endAngle = angle + 20
            const largeArc = 0

            const radius = size === 'sm' ? 8 : size === 'md' ? 9 : 10
            const innerRadius = size === 'sm' ? 6 : size === 'md' ? 7 : 8

            const startX = 12 + radius * Math.cos((startAngle * Math.PI) / 180)
            const startY = 12 + radius * Math.sin((startAngle * Math.PI) / 180)
            const endX = 12 + radius * Math.cos((endAngle * Math.PI) / 180)
            const endY = 12 + radius * Math.sin((endAngle * Math.PI) / 180)

            const innerStartX = 12 + innerRadius * Math.cos((endAngle * Math.PI) / 180)
            const innerStartY = 12 + innerRadius * Math.sin((endAngle * Math.PI) / 180)
            const innerEndX = 12 + innerRadius * Math.cos((startAngle * Math.PI) / 180)
            const innerEndY = 12 + innerRadius * Math.sin((startAngle * Math.PI) / 180)

            const pathData = `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArc} 1 ${endX} ${endY} L ${innerStartX} ${innerStartY} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerEndX} ${innerEndY} Z`

            return (
              <path
                key={segment}
                d={pathData}
                fill={`url(#spinner-gradient-${size})`}
                className={`spinner-segment ${segmentClasses[size]}`}
                style={{
                  animationDelay: `${segment * 0.1}s`,
                }}
              />
            )
          })}
        </svg>
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  )
}
